import { Box, IconButton, Link, Tooltip, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import { Link as RouterLink } from 'react-router-dom';
import type { ReactNode } from 'react';

interface HomeSectionHeaderProps {
  title: string;
  icon: ReactNode;
  href?: string;
  linkLabel?: string;
  onRefresh?: () => void;
  refreshLabel?: string;
  loading?: boolean;
  count?: number | string;
}

export const HomeSectionHeader = ({
  title,
  icon,
  href,
  linkLabel = 'Ver todos',
  onRefresh,
  refreshLabel = 'Actualizar',
  loading = false,
  count,
}: HomeSectionHeaderProps) => {
  const classes = useStyles();
  const linkClasses = useHomeLinkStyles();
  return (
    <Box className={classes.header}>
      <Box className={classes.titleWrap}>
        <Box className={classes.icon}>{icon}</Box>
        <Typography className={classes.title}>{title}</Typography>
        {count !== undefined && (
          <Typography className={classes.count}>{`(${count})`}</Typography>
        )}
        {onRefresh && (
          <Tooltip title={refreshLabel}>
            <span>
              <IconButton
                size="small"
                onClick={onRefresh}
                disabled={loading}
                aria-label={refreshLabel}
              >
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
      {href && (
        <Link component={RouterLink} to={href} className={linkClasses.link}>
          {linkLabel}
        </Link>
      )}
    </Box>
  );
};

export const useHomeLinkStyles = makeStyles(theme => ({
  link: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    fontSize: '0.82rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    '&:hover': { textDecoration: 'underline' },
  },
}));

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.25),
    borderBottom: `1px solid ${theme.palette.border}`,
    paddingBottom: theme.spacing(1),
    '@media (max-width: 600px)': {
      alignItems: 'flex-start',
    },
  },
  titleWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  icon: {
    color: theme.palette.secondary.main,
    flexShrink: 0,
    display: 'flex',
    '& .MuiSvgIcon-root': { fontSize: 18 },
  },
  title: {
    color: theme.palette.text.primary,
    fontFamily: theme.typography.h3.fontFamily,
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.3,
    margin: 0,
  },
  count: {
    color: theme.palette.text.secondary,
    fontSize: theme.typography.fontSize,
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
  },
}));
